import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';

const StoriesReel = ({
  onOpenCreateStory,
  onSelectUserStory,
  refreshTrigger,
  userStories: externalUserStories,
  loading: externalLoading,
  onStoriesLoaded,
  className = '',
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [internalUserStories, setInternalUserStories] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);

  const isControlled = externalUserStories !== undefined;
  const userStories = isControlled ? externalUserStories : internalUserStories;
  const loading = externalLoading !== undefined ? externalLoading : internalLoading;

  useEffect(() => {
    if (!isControlled) {
      fetchStories();
    }
  }, [refreshTrigger, isControlled]);

  const fetchStories = async () => {
    try {
      const res = await axios.get('/api/stories/feed');
      const stories = res.data?.userStories || [];
      setInternalUserStories(stories);
      if (onStoriesLoaded) onStoriesLoaded(stories);
    } catch (err) {
      console.error('Fetch stories error:', err);
    } finally {
      setInternalLoading(false);
    }
  };

  // Find if current user has active stories
  const selfStoryGroup = userStories.find((g) => g.isSelf);
  const otherStoryGroups = userStories.filter((g) => !g.isSelf);

  return (
    <div className={`border-b border-zinc-100 dark:border-zinc-800/80 bg-white dark:bg-black py-3 px-4 ${className}`}>
      <div className="flex items-center gap-3.5 overflow-x-auto no-scrollbar scroll-smooth">
        {/* Your Story / Add Story Button */}
        <div className="flex flex-col items-center flex-shrink-0 cursor-pointer group">
          <div className="relative">
            {selfStoryGroup && selfStoryGroup.stories.length > 0 ? (
              // User has active stories
              <button
                type="button"
                onClick={() => onSelectUserStory(selfStoryGroup, 0, userStories)}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full p-0.5 bg-gradient-to-tr from-sky-400 to-blue-600 transition-transform active:scale-95 flex items-center justify-center"
              >
                <div className="w-full h-full rounded-full overflow-hidden border-2 border-white dark:border-black bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center">
                  {currentUser?.profilePicture ? (
                    <img
                      src={getMediaUrl(currentUser.profilePicture)}
                      alt={currentUser.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="font-bold text-sm text-zinc-700 dark:text-zinc-300">
                      {(currentUser?.username || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              </button>
            ) : (
              // No active stories - show Add button
              <button
                type="button"
                onClick={onOpenCreateStory}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full p-0.5 border-2 border-dashed border-zinc-300 dark:border-zinc-700 hover:border-sky-500 dark:hover:border-sky-500 transition-all flex items-center justify-center active:scale-95"
              >
                <div className="w-full h-full rounded-full overflow-hidden border border-white dark:border-black bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center">
                  {currentUser?.profilePicture ? (
                    <img
                      src={getMediaUrl(currentUser.profilePicture)}
                      alt={currentUser.username}
                      className="w-full h-full object-cover opacity-80"
                    />
                  ) : (
                    <span className="font-bold text-sm text-zinc-700 dark:text-zinc-300">
                      {(currentUser?.username || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              </button>
            )}

            {/* Quick Add Plus Pill */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenCreateStory();
              }}
              className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-sky-500 text-white flex items-center justify-center ring-2 ring-white dark:ring-black hover:bg-sky-600 transition-colors shadow-xs"
              title={t('stories.addStory', 'Add story')}
              aria-label={t('stories.addStory', 'Add story')}
            >
              <i className="fas fa-plus text-[10px]" />
            </button>
          </div>
          <span className="text-[11px] font-medium text-zinc-700 dark:text-zinc-300 mt-1 max-w-[64px] truncate text-center">
            {t('stories.yourStory', 'Your story')}
          </span>
        </div>

        {/* Loading placeholders */}
        {loading && (
          <>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex flex-col items-center flex-shrink-0 animate-pulse">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-zinc-200 dark:bg-zinc-800" />
                <div className="w-10 h-2 bg-zinc-200 dark:bg-zinc-800 rounded mt-1.5" />
              </div>
            ))}
          </>
        )}

        {/* Other Users' Stories */}
        {!loading &&
          otherStoryGroups.map((group) => {
            const hasUnviewed = group.hasUnviewed;
            return (
              <div
                key={group.userId}
                className="flex flex-col items-center flex-shrink-0 cursor-pointer group"
                onClick={() => onSelectUserStory(group, 0, userStories)}
              >
                <div
                  className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full p-0.5 transition-transform group-hover:scale-105 active:scale-95 flex items-center justify-center ${
                    hasUnviewed
                      ? 'bg-gradient-to-tr from-sky-400 via-indigo-500 to-purple-600 shadow-xs'
                      : 'border border-zinc-200 dark:border-zinc-800'
                  }`}
                >
                  <div className="w-full h-full rounded-full overflow-hidden border-2 border-white dark:border-black bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center">
                    {group.profilePicture ? (
                      <img
                        src={getMediaUrl(group.profilePicture)}
                        alt={group.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 flex items-center justify-center font-bold text-sm">
                        {(group.username || group.handle || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
                <span
                  className={`text-[11px] mt-1 max-w-[64px] truncate text-center ${
                    hasUnviewed
                      ? 'font-bold text-zinc-900 dark:text-zinc-100'
                      : 'font-normal text-zinc-500 dark:text-zinc-400'
                  }`}
                >
                  {group.username}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default StoriesReel;
